import { Injectable } from '@angular/core';
import { translate } from '@ngneat/transloco';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { catchError, map, mergeMap, of, withLatestFrom } from 'rxjs';
import { AlertError, AlertSuccess } from '../actions/shared.actions';
import { ApiService } from '../../services/api/api.service';
import {
    DeleteSearchCriteria,
    GetAppSearchFilters,
    SavedSearchCriteria,
    SaveSearchCriteria,
    SetAppSearchFilters,
    SetDefaultSearchFilters,
} from '../actions/search.actions';
import { GetAppMetaData, GetAssetWithLatestVersion } from '../actions/apps.actions';
import { getAppSearchOrganizationMap$ } from '../selectors/search.selector';
import { getAppsMetaDataMap$, getAssetsWithLatestVersion$, getSubscribedAppsMap$ } from '../selectors/apps.selector';
import { getCurrentOrganizationId$ } from '../selectors/session.selector';
import { UtilsService } from '../../services/utils/utils.service';

@Injectable()
export class SearchEffects {
    constructor(private actions$: Actions, private _api: ApiService, private store$: Store, private _utils: UtilsService) {}

    getSearchDefaultFilters$ = createEffect(() =>
        this.actions$.pipe(
            ofType(GetAppSearchFilters),
            withLatestFrom(
                this.store$.select(getSubscribedAppsMap$),
                this.store$.select(getCurrentOrganizationId$),
                this.store$.select(getAppSearchOrganizationMap$),
                this.store$.select(getAssetsWithLatestVersion$)
            ),
            mergeMap(([action, appsMap, organizationId, searchMap, versionsMap]) => {
                if (!versionsMap?.[action.serviceId]?.metadataId) {
                    this.store$.dispatch(
                        GetAssetWithLatestVersion({
                            serviceId: action.serviceId,
                        })
                    );
                    setTimeout(() => {
                        this.store$.dispatch(GetAppSearchFilters(action));
                    }, 500);
                    return [];
                }
                const app = appsMap?.[action.serviceId];
                const defaultFilters = searchMap?.[action.serviceId]?.defaultFilters;
                if (defaultFilters?.['default-filters']?.length > 0) {
                    return [];
                }
                return this._api.instances.getDefaultSearchFilters(app?.assetMetaUId || versionsMap?.[app?.serviceId]?.metadataId).pipe(
                    map(res => {
                        return SetDefaultSearchFilters({
                            serviceId: action.serviceId,
                            organizationId,
                            filters: res?.['default-filters'] || [],
                            criterias: res.filterCriterias,
                        });
                    }),
                    catchError(() => [])
                );
            })
        )
    );

    getAppSearchFilters$ = createEffect(() =>
        this.actions$.pipe(
            ofType(GetAppSearchFilters),
            withLatestFrom(
                this.store$.select(getSubscribedAppsMap$),
                this.store$.select(getAppsMetaDataMap$),
                this.store$.select(getCurrentOrganizationId$),
                this.store$.select(getAppSearchOrganizationMap$),
                this.store$.select(getAssetsWithLatestVersion$)
            ),
            mergeMap(([action, appsMap, metaDataMap, organizationId, searchMap, versionsMap]) => {
                if (!versionsMap?.[action.serviceId]?.metadataId) {
                    this.store$.dispatch(
                        GetAssetWithLatestVersion({
                            serviceId: action.serviceId,
                        })
                    );
                    setTimeout(() => {
                        this.store$.dispatch(GetAppSearchFilters(action));
                    }, 500);
                    return [];
                }
                const app = appsMap?.[action.serviceId];
                const metaData = metaDataMap?.[app?.serviceId]?.[app?.assetMetaUId || versionsMap?.[app?.serviceId]?.metadataId];
                if (app?.serviceId?.length > 0 && !metaData?.uid) {
                    setTimeout(() => {
                        this.store$.dispatch(GetAppSearchFilters(action));
                    }, 500);
                    return of(GetAppMetaData({ serviceId: app?.serviceId }));
                }
                const objectId = `${app?.restApiName}.${metaData?.entities?.find(entity => entity.primary)?.uid}`;
                const searchFilters = searchMap?.[action.serviceId]?.searchFilters;
                if (searchFilters?.length > 0) {
                    return [];
                }
                return this._api.instances.getSearchConfiguration(objectId).pipe(
                    map(res => {
                        return SetAppSearchFilters({
                            serviceId: action.serviceId,
                            organizationId,
                            filters: res?.['searchFilters'] || [],
                        });
                    }),
                    catchError(() => [])
                );
            })
        )
    );

    saveSearchCriteria$ = createEffect(() =>
        this.actions$.pipe(
            ofType(SaveSearchCriteria),
            withLatestFrom(
                this.store$.select(getSubscribedAppsMap$),
                this.store$.select(getAppsMetaDataMap$),
                this.store$.select(getCurrentOrganizationId$),
                this.store$.select(getAppSearchOrganizationMap$),
                this.store$.select(getAssetsWithLatestVersion$)
            ),
            mergeMap(([action, appsMap, metaDataMap, organizationId, searchMap, versionsMap]) => {
                if (!versionsMap?.[action.serviceId]?.metadataId) {
                    this.store$.dispatch(GetAssetWithLatestVersion({ serviceId: action.serviceId }));
                    setTimeout(() => {
                        this.store$.dispatch(SaveSearchCriteria(action));
                    }, 500);
                    return [];
                }
                const app = appsMap?.[action.serviceId];
                const metaData = metaDataMap?.[app?.serviceId]?.[app?.assetMetaUId || versionsMap?.[app?.serviceId]?.metadataId];
                const objectId = `${app?.restApiName}.${metaData?.entities?.find(entity => entity.primary)?.uid}`;
                const searchFilters = searchMap?.[action.serviceId]?.searchFilters || [];
                return this._api.instances
                    .saveSearchConfiguration({
                        criteria: action.criteria,
                        filterName: action.filterName,
                        objectId: objectId,
                        id: action.id,
                    })
                    .pipe(
                        map((res: any) => {
                            this.store$.dispatch(SavedSearchCriteria({ criteria: res?.response, serviceId: action.serviceId }));
                            return SetAppSearchFilters({
                                serviceId: action.serviceId,
                                organizationId,
                                filters: [res?.response, ...searchFilters.filter(filter => filter.id !== action.id)],
                            });
                        }),
                        catchError(e => {
                            const message = (e?.msg && this._utils.destructStringedArray(e?.msg)) || translate('Failed to save filter');
                            return of(AlertError({ message }));
                        })
                    );
            })
        )
    );

    deleteSearchCriteria$ = createEffect(() =>
        this.actions$.pipe(
            ofType(DeleteSearchCriteria),
            withLatestFrom(this.store$.select(getCurrentOrganizationId$), this.store$.select(getAppSearchOrganizationMap$)),
            mergeMap(([action, organizationId, searchMap]) => {
                const searchFilters = searchMap?.[action.serviceId]?.searchFilters || [];
                const filter = searchFilters.find(criteria => criteria.id === action.id);
                return this._api.instances
                    .deleteSearchConfiguration({
                        id: action.id,
                    })
                    .pipe(
                        map(() => {
                            this.store$.dispatch(AlertSuccess({ message: translate('Deleted') + ' ' + filter.filterName }));
                            return SetAppSearchFilters({
                                serviceId: action.serviceId,
                                organizationId,
                                filters: searchFilters.filter(criteria => criteria.id !== action.id),
                            });
                        }),
                        catchError(() => {
                            return [];
                        })
                    );
            })
        )
    );
}
